import React, {createContext, useContext, useState, ReactNode} from 'react'
import {KTSVG} from '_metronic/helpers'
import {Divider, Spin} from 'antd'
import {Modal, Spinner} from 'react-bootstrap'

// Definisikan type untuk context
interface ModalContextType {
  showModal: (onConfirm?: () => Promise<void> | void, title?: string, body?: string | ReactNode, submitText?: string) => void
  successModal: (onConfirm?: () => Promise<void> | void, title?: string, body?: string | ReactNode) => void
  hideModal: () => void
}

// Inisialisasi context
const ModalContext = createContext<ModalContextType | undefined>(undefined)

// Provider untuk modal
export const ModalProvider = ({children}: {children: ReactNode}) => {
  const [isConfirmModalVisible, setConfirmModalVisible] = useState<boolean>(false)
  const [isInfoModalVisible, setInfoModalVisible] = useState<boolean>(false)
  const [onConfirm, setOnConfirm] = useState<(() => Promise<void> | void) | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string | null>(null)
  const [okText, setOkText] = useState<string | null>(null)
  const [modalContent, setModalContent] = useState<string | ReactNode | null>(null)

  const showModal = (confirmCallback?: () => Promise<void> | void, title?: string, body?: string | ReactNode, submitText?: string) => {
    setOnConfirm(() => confirmCallback || null)
    setModalTitle(title || 'Konfirmasi')
    setModalContent(body || null)
    setOkText(submitText || 'Menghapus...')
    setConfirmModalVisible(true)
  }

  const successModal = async (confirmCallback?: () => Promise<void> | void, title?: string, body?: string | ReactNode) => {
    setOnConfirm(() => confirmCallback || null)
    setModalTitle(title || 'Informasi')
    setModalContent(body || 'Proses selesai')
    setInfoModalVisible(true)
  }

  const hideModal = () => {
    setConfirmModalVisible(false)
    setInfoModalVisible(false)
    setModalTitle(null)
    setModalContent(null)
    setOnConfirm(null)
    setIsLoading(false)
  }

  const handleConfirm = async () => {
    if (onConfirm) {
      setIsLoading(true)
      try {
        await onConfirm()
      } catch (error) {
        console.error('Error onConfirm:', error)
      }
      setIsLoading(false)
      hideModal()
    }
  }

  return (
    <ModalContext.Provider value={{showModal, successModal, hideModal}}>
      {children}

      {/* Modal Konfirmasi */}
      <Modal show={isConfirmModalVisible} onHide={hideModal} animation={false} centered className='ops-modal-confirm' backdrop='static' size='sm'>
        <Modal.Body className='p-0 py-5'>
          <div className='d-flex justify-content-between align-items-center ps-6 pe-3'>
            <h3 className='fw-bold mb-0' style={{letterSpacing: -0.5}}>
              {modalTitle ?? 'Konfirmasi'}
            </h3>
            <div className=''>
              <div className='btn btn-icon btn-sm btn-active-light-primary' onClick={hideModal}>
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
              </div>
            </div>
          </div>
          <Divider className='mt-4 mb-4' />
          <div className='text-center mt-7'>
            <i className='fas fa-triangle-exclamation ms-1 text-warning' style={{fontSize: 80}}></i>
          </div>
          <div className='d-flex justify-content-center mb-7'>
            <div>{modalContent ? modalContent : <p className='mb-0 fs-4'>Apakah kamu yakin ingin menghapus?</p>}</div>
          </div>

          <Divider className='mt-4 mb-4' />
          <div className='marvel-modal-footer text-end px-5'>
            <button type='button' className='btn btn-primary btn-sm rounded-0 fs-6 px-7 me-2' onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation='border' size='sm' role='status' aria-hidden='true' />
                  <span className='ms-2'>{okText ?? 'Menghapus...'}</span>
                </>
              ) : (
                'Ya'
              )}
            </button>
            <button type='button' className='btn btn-danger btn-sm rounded-0 fs-6 px-7' onClick={hideModal} disabled={isLoading}>
              Tidak
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal Informasi */}
      <Modal show={isInfoModalVisible} onHide={hideModal} animation={false} centered className='ops-modal-info' backdrop='static' size='lg'>
        <Modal.Body className='p-0 py-5'>
          <div className='d-flex justify-content-between align-items-center ps-6 pe-3'>
            <h3 className='fw-bold mb-0' style={{letterSpacing: -0.5}}>
              {modalTitle ?? 'Informasi'}
            </h3>
            <div className=''>
              {/* <div className='btn btn-icon btn-sm btn-active-light-primary' onClick={hideModal}>
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
              </div> */}
            </div>
          </div>
          <Divider className='mt-4 mb-4' />
          <div className='text-center mt-7 px-10'>
            <i className='fas fa-check ms-1 text-success px-10' style={{fontSize: 80}}></i>
          </div>
          {/* <div className='text-center mt-7'>
            <i className='fas fa-check ms-1 text-primary' style={{fontSize: 80}}></i>
            <div className='w-250px'>
              <Spin indicator={<span className='custom-spinner' />} tip={<p className=''>Loading...</p>} />
            </div>
          </div> */}
          <div className='d-flex justify-content-center mb-7'>
            <div className='fs-4'>
              <p className='text-center px-5 py-5 fs-3'>{modalContent}</p>
            </div>
          </div>

          <Divider className='mt-4 mb-4' />
          <div className='text-end px-5'>
            <button type='button' className='btn btn-primary btn-sm rounded-0 fs-6 px-15' onClick={handleConfirm}>
              Ok
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </ModalContext.Provider>
  )
}

// Custom hook untuk memudahkan akses context
export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

// import React, {createContext, useContext, useState, ReactNode} from 'react'
// import {KTSVG} from '_metronic/helpers'
// import {Divider} from 'antd'
// import {Modal, Spinner} from 'react-bootstrap'

// // Definisikan type untuk context
// interface ModalContextType {
//   showModal: (onConfirm?: () => Promise<void> | void, title?: string, body?: string | ReactNode, submitText?: string) => void // Tambahkan content sebagai parameter
//   hideModal: () => void
// }

// // Inisialisasi context
// const ModalContext = createContext<ModalContextType | undefined>(undefined)

// // Provider untuk modal
// export const ModalProvider = ({children}: {children: ReactNode}) => {
//   const [isShow, setIsShow] = useState<boolean>(false)
//   // const [modalContent, setModalContent] = useState<ReactNode>(null)
//   const [onConfirm, setOnConfirm] = useState<(() => Promise<void> | void) | null>(null)
//   const [isLoading, setIsLoading] = useState<boolean>(false) // Tambahkan isLoading untuk handle loading
//   const [modalTitle, setModalTitle] = useState<string | null>(null)
//   const [okText, setOkText] = useState<string | null>(null)
//   const [modalContent, setModalContent] = useState<string | ReactNode | null>(null)

//   const showModal = (confirmCallback?: () => Promise<void> | void, title?: string, body?: string | ReactNode, submitText?: string) => {
//     setOnConfirm(() => confirmCallback || null)
//     setModalTitle(title || null)
//     setModalContent(body || null)
//     setOkText(submitText || null)
//     setIsShow(true)
//   }

//   // Fungsi untuk menyembunyikan modal
//   const hideModal = () => {
//     setIsShow(false)
//     setModalTitle(null)
//     setModalContent(null)
//     setOnConfirm(null) // Reset callback
//     setIsLoading(false) // Reset loading
//   }

//   // Fungsi yang dipanggil saat tombol "Ya" diklik
//   const handleConfirm = async () => {
//     if (onConfirm) {
//       setIsLoading(true) // Set loading jadi true saat mulai
//       try {
//         await onConfirm() // Eksekusi callback (bisa async)
//       } catch (error) {
//         console.error('Error onConfirm:', error)
//       }
//       setIsLoading(false) // Setelah selesai, loading jadi false
//       hideModal() // Sembunyikan modal setelah konfirmasi
//     }
//   }

//   return (
//     <ModalContext.Provider value={{showModal, hideModal}}>
//       {children}
//       <Modal show={isShow} onHide={hideModal} animation={false} centered className='ops-modal-confirm' backdrop='static' size='sm'>
//         <Modal.Body className='p-0 py-5'>
//           <div className='d-flex justify-content-between align-items-center ps-6 pe-3'>
//             <h3 className='fw-bold mb-0' style={{letterSpacing: -0.5}}>
//               {modalTitle ?? 'Konfirmasi'}
//             </h3>
//             <div className=''>
//               <div className='btn btn-icon btn-sm btn-active-light-primary' onClick={hideModal}>
//                 <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
//               </div>
//             </div>
//           </div>
//           <Divider className='mt-4 mb-4' />
//           <div className='text-center mt-7'>
//             <i className='fas fa-triangle-exclamation ms-1 text-warning' style={{fontSize: 80}}></i>
//           </div>
//           <div className='d-flex justify-content-center mb-7'>
//             <div>
//               {modalContent ? (
//                 modalContent
//               ) : (
//                 <>
//                   <br />
//                   <p className='mb-0 fs-4'>Apakah kamu yakin ingin menghapus?</p>
//                 </>
//               )}
//             </div>
//           </div>

//           <Divider className='mt-4 mb-4' />
//           <div className='marvel-modal-footer text-end px-5'>
//             <button
//               type='button'
//               className='btn btn-primary btn-sm rounded-0 fs-6 px-7 me-2'
//               onClick={handleConfirm}
//               disabled={isLoading} // Disable button saat loading
//             >
//               {isLoading ? (
//                 <>
//                   <Spinner animation='border' size='sm' role='status' aria-hidden='true' />
//                   <span className='ms-2'>{okText ?? 'Menghapus...'}</span>
//                 </>
//               ) : (
//                 'Ya'
//               )}
//             </button>
//             <button type='button' className='btn btn-danger btn-sm rounded-0 fs-6 px-7' onClick={hideModal} disabled={isLoading}>
//               Tidak
//             </button>
//           </div>
//         </Modal.Body>
//       </Modal>
//     </ModalContext.Provider>
//   )
// }

// // Custom hook untuk memudahkan akses context
// export const useModal = () => {
//   const context = useContext(ModalContext)
//   if (!context) {
//     throw new Error('useModal must be used within a ModalProvider')
//   }
//   return context
// }
